import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Employee, License } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Search, UserPlus, Users, CheckCircle, Save, X } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export default function LicenseAssignEmployees() {
  const { id } = useParams<{ id: string }>();
  const licenseId = parseInt(id);
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  
  // Query license details
  const { data: license, isLoading: licenseLoading } = useQuery<License>({
    queryKey: ['/api/licenses', licenseId],
    queryFn: async () => {
      const res = await fetch(`/api/licenses/${licenseId}`);
      if (!res.ok) throw new Error("Failed to fetch license");
      return res.json();
    },
    enabled: !isNaN(licenseId),
  });
  
  // Query all employees in tenant
  const { data: allEmployees = [], isLoading: employeesLoading } = useQuery<Employee[]>({
    queryKey: ['/api/employees'],
    queryFn: async () => {
      const res = await fetch(`/api/employees`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !isNaN(licenseId),
  });
  
  // Query assigned employees for this license
  const { data: assignedEmployees = [], isLoading: assignedLoading } = useQuery<Employee[]>({
    queryKey: ['/api/licenses', licenseId, 'employees'],
    queryFn: async () => {
      const res = await fetch(`/api/licenses/${licenseId}/employees`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !isNaN(licenseId),
  });
  
  // Filter employees based on search term and exclude already assigned ones
  const getFilteredEmployees = (includeAssigned = false) => {
    return allEmployees.filter(employee => {
      // If we're not including already assigned and the employee is already assigned, exclude them
      const isAssigned = assignedEmployees.some(assigned => assigned.id === employee.id);
      if (!includeAssigned && isAssigned) return false;
      
      const fullName = employee.fullName || '';
      
      const matchesSearch = searchTerm === "" || 
        fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (employee.email && employee.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (employee.jobTitle && employee.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesSearch;
    });
  };
  
  const availableEmployees = getFilteredEmployees(false);
  
  // Initialize selected employees based on assigned employees
  useEffect(() => {
    if (assignedEmployees && assignedEmployees.length > 0) {
      setSelectedEmployees(assignedEmployees.map(emp => emp.id));
    }
  }, [assignedEmployees]);
  
  // Mutation to assign a single employee to the license
  const assignMutation = useMutation({
    mutationFn: async (employeeId: number) => {
      console.log("Adding employee assignment:", employeeId);
      const response = await fetch(`/api/license-employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId, employeeId })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Failed to add assignment:", errorData);
        throw new Error(errorData.message || "Failed to add employee assignment");
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId, 'employees'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      toast({
        title: "Employee assigned successfully",
        description: "The employee has been assigned to this license",
        variant: "default",
      });
      setSelectedEmployee(null);
    },
    onError: (error) => {
      console.error("Error assigning employee:", error);
      toast({
        title: "Error assigning employee",
        description: "There was a problem assigning the employee to this license",
        variant: "destructive",
      });
    }
  });

  // Mutation to remove an employee from the license
  const removeMutation = useMutation({
    mutationFn: async (employeeId: number) => {
      console.log("Removing employee assignment:", employeeId);
      const response = await fetch(`/api/license-employees`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId, employeeId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Failed to remove assignment:", errorData);
        throw new Error(errorData.message || "Failed to remove employee assignment");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId, 'employees'] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses', licenseId] });
      queryClient.invalidateQueries({ queryKey: ['/api/licenses'] });
      queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      toast({
        title: "Employee removed",
        description: "The employee has been removed from this license",
        variant: "default",
      });
    },
    onError: (error) => {
      console.error("Error removing employee:", error);
      toast({
        title: "Error removing employee",
        description: "There was a problem removing the employee from this license",
        variant: "destructive",
      });
    }
  });
  
  // Calculate available seats
  const getAvailableSeats = () => {
    if (!license) return 0;
    return license.totalSeats - (assignedEmployees?.length || 0);
  };
  
  const isLoading = licenseLoading || employeesLoading || assignedLoading;
  
  // Format employee name for display
  const formatEmployeeName = (employee: Employee) => {
    return employee.fullName || employee.email || `Employee ${employee.id}`;
  };
  
  // Check if we can assign more employees
  const canAssignMore = license && getAvailableSeats() > 0;
  
  // Handle assigning a selected employee
  const handleAssignEmployee = () => {
    if (selectedEmployee) {
      assignMutation.mutate(selectedEmployee.id);
    }
  };
  
  return (
    <div className="container max-w-4xl mx-auto mt-4">
      <div className="flex items-center mb-6">
        <Link to={`/licenses/${licenseId}`}>
          <Button variant="ghost" size="sm" className="mr-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to License
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">Assign Employees to License</h1>
      </div>
      
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      ) : license ? (
        <>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{license.name}</CardTitle>
              <CardDescription>
                {assignedEmployees.length} of {license.totalSeats} seats used
                • Renewal: {formatDate(license.renewalDate || null)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Available Seats</p>
                  <p className="text-xl font-medium">{getAvailableSeats()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Assigned Employees</p>
                  <p className="text-xl font-medium">{assignedEmployees.length}</p>
                </div>
              </div>
              
              {/* Warning if all seats are used */}
              {getAvailableSeats() <= 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm">
                  <strong>Warning:</strong> All seats for this license are currently assigned. 
                  To add more employees, you'll need to either remove some assignments or upgrade your license.
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Employee Selection with Searchable Dropdown */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Add Employee</CardTitle>
              <CardDescription>
                Assign a new employee to this license
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      disabled={!canAssignMore || assignMutation.isPending}
                      className="w-full justify-between"
                    >
                      {selectedEmployee ? formatEmployeeName(selectedEmployee) : "Select an employee..."}
                      <UserPlus className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0">
                    <Command>
                      <CommandInput 
                        placeholder="Search employees..." 
                        value={searchTerm}
                        onValueChange={setSearchTerm}
                      />
                      <CommandList>
                        <CommandEmpty>No employees found</CommandEmpty>
                        <CommandGroup>
                          {availableEmployees.map((employee) => (
                            <CommandItem
                              key={employee.id}
                              onSelect={() => {
                                setSelectedEmployee(employee);
                                setOpen(false);
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{formatEmployeeName(employee)}</span>
                                <span className="text-xs text-muted-foreground">{employee.email}</span>
                                {employee.jobTitle && (
                                  <span className="text-xs text-muted-foreground">{employee.jobTitle}</span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                
                {selectedEmployee && (
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1"
                      onClick={handleAssignEmployee}
                      disabled={assignMutation.isPending}
                    >
                      {assignMutation.isPending ? "Assigning..." : "Assign Employee"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedEmployee(null)}
                      disabled={assignMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                
                {!canAssignMore && (
                  <p className="text-center text-sm text-muted-foreground">
                    No seats available. Remove employees or upgrade your license to add more.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Currently Assigned Employees List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Assigned Employees</CardTitle>
              <CardDescription>
                Employees currently assigned to this license
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignedEmployees.length > 0 ? (
                <div className="border rounded-md divide-y">
                  {assignedEmployees.map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center">
                        <div className="bg-slate-200 rounded-full h-8 w-8 flex items-center justify-center mr-3 flex-shrink-0">
                          {(employee.fullName || employee.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">{formatEmployeeName(employee)}</div>
                          <div className="text-sm text-muted-foreground">
                            {employee.email}
                            {employee.jobTitle && <span className="ml-2">• {employee.jobTitle}</span>}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeMutation.mutate(employee.id)}
                        disabled={removeMutation.isPending}
                      >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Remove</span>
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-6 border rounded-md text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-20" />
                  <p>No employees are currently assigned to this license</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center p-8 bg-white rounded-md border shadow-sm">
          <p className="text-lg font-medium mb-2">License Not Found</p>
          <p className="text-muted-foreground mb-4">The license you're looking for doesn't exist or you don't have permission to view it.</p>
          <Link to="/licenses">
            <Button>Go to Licenses</Button>
          </Link>
        </div>
      )}
    </div>
  );
}